import { useState } from 'react';
import toast from 'react-hot-toast';

import { IconClose } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { fmtMoney, digitsOnly, groupThousands } from '../../../common/utils/money';
import { formatDateOnly } from '../../../common/utils/date';
import { useValuations, useCreateValuation, useUpdateValuation } from '../contexts/valuations';

import './styles/valuation-form.css';

const today = () => new Date().toLocaleDateString('en-CA');
const BASES = [
    { value: 'manual',    label: 'Manual' },
    { value: 'market',    label: 'Mercado' },
    { value: 'appraisal', label: 'Avalúo (perito)' },
];

// Input de dinero amigable ("$", miles con separador; guarda solo dígitos).
function MoneyInput({ value, onChange, disabled }) {
    return (
        <span className="val-money">
            <span className="val-money__sign">$</span>
            <input type="text" inputMode="numeric" value={groupThousands(value)} onChange={(e) => onChange(digitsOnly(e.target.value))} disabled={disabled} placeholder="0" />
        </span>
    );
}

// Formulario de AVALÚO de un SUJETO — cría/vaca (kind 'animals') o preñez (kind 'pregnancies')
// (0098/0108). `subject = { kind, id, label }`. Alta (POST) o edición (PATCH). `basis` = origen
// técnico (manual/market/appraisal); `cause` = narrativa de negocio visible al cliente (0108,
// columna propia, ortogonal a basis). El backend congela/prela.
export default function ValuationForm({ subject, valuation, onClose, onSaved }) {
    const { kind, id, label } = subject;
    const editing = !!valuation;
    const { data } = useValuations(kind, id);
    const latest = data?.latest;
    const { mutateAsync: create, isPending: creating } = useCreateValuation(kind, id);
    const { mutateAsync: update, isPending: updating } = useUpdateValuation(kind, id);
    const busy = creating || updating;
    const cardRef = useModalDialog(onClose);

    const [value, setValue] = useState(editing && valuation.estimated_value != null ? digitsOnly(String(valuation.estimated_value)) : '');
    const [date, setDate] = useState(editing ? (valuation.valuation_date || today()) : today());
    const [basis, setBasis] = useState(editing ? (valuation.basis || 'manual') : 'manual');
    const [cause, setCause] = useState(editing ? (valuation.cause || '') : '');
    const [remarks, setRemarks] = useState(editing && typeof valuation.remarks === 'string' ? valuation.remarks : '');

    const submit = async () => {
        if (value === '' || value == null) { toast.error('Ingresá el valor estimado'); return; }
        if (!(Number(value) >= 0)) { toast.error('El valor debe ser ≥ 0'); return; }
        if (!date) { toast.error('Elegí la fecha del avalúo'); return; }
        const payload = { estimated_value: Number(value), valuation_date: date, basis };
        if (editing) {
            payload.cause = cause.trim() || null;      // permite limpiar en edición
            payload.remarks = remarks.trim() || null;
        } else {
            if (cause.trim()) payload.cause = cause.trim();
            if (remarks.trim()) payload.remarks = remarks.trim();
        }
        try {
            if (editing) await update({ vid: valuation.id, payload });
            else await create(payload);
            toast.success(editing ? 'Avalúo actualizado' : 'Avalúo registrado');
            onSaved?.();
        } catch (er) {
            if (er?.status !== 403) toast.error(er?.message || 'No se pudo guardar el avalúo');
        }
    };

    return (
        <div className="val-modal" role="dialog" aria-modal="true" aria-label={`Avaluar ${label}`}>
            <div className="val-modal__overlay" onClick={onClose} aria-hidden="true" />
            <div className="val-modal__card" ref={cardRef}>
                <header className="val-modal__head">
                    <div>
                        <h2 className="val-modal__title">{editing ? 'Editar avalúo' : 'Avalúo'} · {label}</h2>
                        <span className="val-modal__sub">Valor de inventario estimado.</span>
                    </div>
                    <button type="button" className="val-modal__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                {!editing && latest && (
                    <p className="val-modal__current">
                        Valor actual: <strong>{fmtMoney(latest.estimated_value)}</strong>
                        <span> · {formatDateOnly(latest.valuation_date)}</span>
                    </p>
                )}

                <div className="val-form">
                    <div className="val-form__row">
                        <label className="val-form__field">
                            <span className="val-form__label">Valor estimado</span>
                            <MoneyInput value={value} onChange={setValue} disabled={busy} />
                        </label>
                        <label className="val-form__field">
                            <span className="val-form__label">Fecha</span>
                            <input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} disabled={busy} />
                        </label>
                    </div>

                    <label className="val-form__field">
                        <span className="val-form__label">Base <span className="val-form__hint">(origen técnico)</span></span>
                        <select value={basis} onChange={(e) => setBasis(e.target.value)} disabled={busy}>
                            {BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                        </select>
                    </label>

                    <label className="val-form__field">
                        <span className="val-form__label">Causa <span className="val-form__hint">(motivo, visible al cliente)</span></span>
                        <input type="text" maxLength={200} value={cause} onChange={(e) => setCause(e.target.value)} disabled={busy} placeholder="Ej. revalorización post-parto, avalúo por feria…" />
                    </label>

                    <label className="val-form__field">
                        <span className="val-form__label">Notas <span className="val-form__hint">(internas)</span></span>
                        <textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={busy} placeholder="Opcional" />
                    </label>
                </div>

                <footer className="val-modal__foot">
                    <button type="button" className="val-btn val-btn--ghost" onClick={onClose} disabled={busy}>Cancelar</button>
                    <button type="button" className="val-btn val-btn--primary" onClick={submit} disabled={busy || value === ''}>{busy ? 'Guardando…' : editing ? 'Guardar' : 'Registrar avalúo'}</button>
                </footer>
            </div>
        </div>
    );
}
